import { forwardRef, useEffect, useState, type FocusEvent } from 'react';
import { cn } from '@/lib/utils';
import { normalizeDateISO } from '@/lib/datetime';
import { Input, type InputProps } from './input';

// DateInputISO renders an ISO 8601 YYYY-MM-DD date field. We avoid
// `<input type="date">` for the same reason we avoid `<input type="time">`:
// the rendered format follows the browser/OS locale (en-US users see
// "mm/dd/yyyy", everyone else sees something different), there's no HTML
// attribute that forces a locale-independent format, and SportAdmin is
// Nordic-focused so a mixed-format UI is its own confusion vector.
//
// The stored value is always ISO `YYYY-MM-DD` — same as `<input type="date">`
// emits in its `.value`, same as the backend expects. Callers don't notice
// the swap.
//
// On blur we auto-pad short components ("2026-5-3" → "2026-05-03") but
// don't try to be smart about partial input — incomplete strings stay
// as-typed so form-level validation can surface them.

export interface DateInputISOProps
  extends Omit<InputProps, 'type' | 'value' | 'onChange'> {
  value: string;
  onChange: (next: string) => void;
}

export const DateInputISO = forwardRef<HTMLInputElement, DateInputISOProps>(
  ({ value, onChange, onBlur, className, ...props }, ref) => {
    const [internal, setInternal] = useState(value);
    useEffect(() => {
      setInternal(value);
    }, [value]);

    return (
      <Input
        ref={ref}
        type="text"
        inputMode="numeric"
        pattern="\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])"
        maxLength={10}
        placeholder="YYYY-MM-DD"
        autoComplete="off"
        spellCheck={false}
        className={cn('tabular-nums', className)}
        value={internal}
        onChange={(e) => {
          // Run normalize on every keystroke so eight straight digits flip
          // to "YYYY-MM-DD" the moment the user finishes typing the day,
          // without waiting for blur. Manual hyphenated input falls through
          // unchanged (normalize is a no-op when the value already has the
          // shape it wants).
          const next = normalizeDateISO(e.target.value);
          setInternal(next);
          onChange(next);
        }}
        onBlur={(e: FocusEvent<HTMLInputElement>) => {
          const normalized = normalizeDateISO(e.target.value);
          if (normalized !== e.target.value) {
            setInternal(normalized);
            onChange(normalized);
          }
          onBlur?.(e);
        }}
        {...props}
      />
    );
  },
);
DateInputISO.displayName = 'DateInputISO';
